import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from '@tiptap/markdown';
import { useEffect, useRef } from 'react';
import TipTapToolbar from './TipTapToolbar';
import './TipTapEditor.css';

interface TipTapEditorProps {
  markdown: string;
  onMarkdownChange: (markdown: string) => void;
}

export default function TipTapEditor({ markdown, onMarkdownChange }: TipTapEditorProps) {
  const lastMarkdownRef = useRef<string>(markdown);
  const isExternalChangeRef = useRef(false);
  // const editorKey = markdown ? 'with-content' : 'empty'; // Removed unused variable

  const editor = useEditor(
    {
      extensions: [StarterKit, Markdown],
      content: markdown || '',
      contentType: markdown ? 'markdown' : 'html', // Parse as markdown if we have content
      onUpdate: ({ editor }) => {
        // Skip updates that come from external changes
        if (isExternalChangeRef.current) {
          isExternalChangeRef.current = false;
          return;
        }

        // Serialize editor content to markdown
        try {
          // When Markdown extension is used, the editor has a getMarkdown method
          const editorMarkdown = (editor as any).getMarkdown?.() || '';
          if (editorMarkdown !== lastMarkdownRef.current) {
            lastMarkdownRef.current = editorMarkdown;
            onMarkdownChange(editorMarkdown);
          }
        } catch (e) {
          console.error('TipTapEditor: Error serializing markdown:', e);
        }
      },
    },
    [] // Initialize once
  );

  // Sync editor content when markdown prop changes
  useEffect(() => {
    if (editor && markdown !== lastMarkdownRef.current) {
      // Prevent the onUpdate loop from firing for this change
      isExternalChangeRef.current = true;

      // Store the new value
      lastMarkdownRef.current = markdown;

      // Update editor
      const currentPos = editor.state.selection.anchor;
      editor.commands.setContent(markdown);

      // Try to restore selection/cursor position if possible (optional refinement)
      // editor.commands.setTextSelection(currentPos);
    }
  }, [markdown, editor]);

  if (!editor) {
    return <div className="tiptap-editor-loading">Loading editor...</div>;
  }

  return (
    <div className="tiptap-editor-container">
      <TipTapToolbar editor={editor} />
      <EditorContent editor={editor} className="tiptap-editor-content" />
    </div>
  );
}
